import type { AstNode, GrammarConfig } from "langium";
import { AbstractFormatter, Formatting, type LangiumServices } from "langium/lsp";
import {
	type Alias,
	type Directive,
	type DirectiveParameter,
	type Endpoint,
	type Enum,
	type EnumMember,
	type Exception,
	type Field,
	type Import,
	type ImportMember,
	type Module,
	type ReferenceDirective,
	type ReferenceDirectiveArgument,
	type ReferenceType,
	type ReferenceTypeOption,
	type Scalar,
	type ScalarOption,
	type Struct,
	isAlias,
	isDirective,
	isDirectiveParameter,
	isEndpoint,
	isEnum,
	isEnumMember,
	isException,
	isField,
	isImport,
	isImportMember,
	isModule,
	isReferenceDirective,
	isReferenceDirectiveArgument,
	isReferenceType,
	isReferenceTypeOption,
	isScalar,
	isScalarOption,
	isStruct,
} from "../generated/ast.js";

export class IprotoFormatter extends AbstractFormatter {
	protected readonly grammarConfig: () => GrammarConfig;

	constructor(services: LangiumServices) {
		super();

		this.grammarConfig = () => services.parser.GrammarConfig;
	}

	private formatDirectives(node: Module | Scalar | Struct | Enum | EnumMember | Exception | Endpoint | Field) {
		const formatter = this.getNodeFormatter(node);

		node.directives.map((directive, index) => {
			if (index !== 0) {
				formatter.node(directive).prepend(Formatting.oneSpace());
			} else {
				formatter.node(directive).prepend(Formatting.noIndent());
			}
		});
	}

	private formatModule(node: Module) {
		const formatter = this.getNodeFormatter(node);

		formatter.keyword("module").prepend(Formatting.noIndent()).append(Formatting.oneSpace());
		formatter.property("name").append(Formatting.newLines(2));

		this.formatDirectives(node);
	}

	private formatImport(node: Import) {
		const formatter = this.getNodeFormatter(node);

		formatter.keyword("use").prepend(Formatting.noIndent()).append(Formatting.oneSpace());
		formatter.property("target").append(Formatting.noSpace());
		formatter.keyword("::").surround(Formatting.noSpace());
		formatter.keyword("{").append(Formatting.oneSpace());
		formatter.keyword("}").prepend(Formatting.oneSpace());
		formatter.nodes(...node.members).surround(Formatting.oneSpace());
	}

	private formatImportMember(node: ImportMember) {
		const formatter = this.getNodeFormatter(node);

		formatter.keyword("as").surround(Formatting.oneSpace());
	}

	private formatScalar(node: Scalar) {
		const formatter = this.getNodeFormatter(node);

		formatter.keyword("scalar").prepend(Formatting.noIndent()).append(Formatting.oneSpace());
		formatter.keywords("<", ".", "[").surround(Formatting.noSpace());
		formatter.keywords(">", "]").prepend(Formatting.noSpace());
		formatter.keyword("...").prepend(Formatting.oneSpace());

		node.parameters.map((node, index) => {
			if (index !== 0) {
				formatter.node(node).prepend(Formatting.oneSpace());
			}
		});

		node.options.map((node, index) => {
			if (index !== 0) {
				formatter.node(node).prepend(Formatting.oneSpace());
			}
		});

		this.formatDirectives(node);
	}

	private formatScalarOption(node: ScalarOption) {
		const formatter = this.getNodeFormatter(node);
		formatter.keyword(":").surround(Formatting.noSpace());
	}

	private formatEnum(node: Enum) {
		const formatter = this.getNodeFormatter(node);

		formatter.keyword("enum").prepend(Formatting.noIndent()).append(Formatting.oneSpace());
		formatter.property("name").append(Formatting.oneSpace());

		const bracesOpen = formatter.keyword("{");
		const bracesClose = formatter.keyword("}").prepend(Formatting.newLine());

		formatter.interior(bracesOpen, bracesClose).prepend(Formatting.indent());

		this.formatDirectives(node);
	}

	private formatEnumMember(node: EnumMember) {
		this.formatDirectives(node);
	}

	private formatStruct(node: Struct) {
		const formatter = this.getNodeFormatter(node);

		formatter.keyword("struct").prepend(Formatting.noIndent()).append(Formatting.oneSpace());
		formatter.properties("name").append(Formatting.oneSpace());
		formatter.keyword("with").append(Formatting.oneSpace());

		node.mixins.map((node) => {
			formatter.node(node).append(Formatting.oneSpace());
		});

		const bracesOpen = formatter.keyword("{");
		const bracesClose = formatter.keyword("}").prepend(Formatting.newLine());

		formatter.interior(bracesOpen, bracesClose).prepend(Formatting.indent());

		this.formatDirectives(node);
	}

	private formatException(node: Exception) {
		const formatter = this.getNodeFormatter(node);

		formatter.keyword("exception").prepend(Formatting.noIndent()).append(Formatting.oneSpace());
		formatter.property("name").append(Formatting.oneSpace());

		const bracesOpen = formatter.keyword("{");
		const bracesClose = formatter.keyword("}").prepend(Formatting.newLine());

		formatter.interior(bracesOpen, bracesClose).prepend(Formatting.indent());

		this.formatDirectives(node);
	}

	private formatEndpoint(node: Endpoint) {
		const formatter = this.getNodeFormatter(node);

		formatter.keyword("endpoint").prepend(Formatting.noIndent()).append(Formatting.oneSpace());
		formatter.property("name").append(Formatting.noSpace());
		formatter.keywords("(").surround(Formatting.noSpace());
		formatter.keyword(")").prepend(Formatting.noSpace());
		formatter.keyword(":").append(Formatting.oneSpace()).prepend(Formatting.noSpace());

		this.formatDirectives(node);
	}

	private formatDirective(node: Directive) {
		const formatter = this.getNodeFormatter(node);

		formatter.keyword("directive").prepend(Formatting.noIndent()).append(Formatting.oneSpace());

		formatter
			.keywords("on", "module", "scalar", "enum", "variant", "struct", "field", "exception", "endpoint")
			.prepend(Formatting.oneSpace());
		formatter.keyword("(").surround(Formatting.noSpace());
		formatter.keyword(")").prepend(Formatting.noSpace());

		node.parameters.map((node, index) => {
			if (index !== 0) {
				formatter.node(node).prepend(Formatting.oneSpace());
			}
		});
	}

	private formatDirectiveParameter(node: DirectiveParameter) {
		const formatter = this.getNodeFormatter(node);

		formatter.keywords(":", "?").surround(Formatting.noSpace());
	}

	private formatAlias(node: Alias) {
		const formatter = this.getNodeFormatter(node);

		formatter.keyword("alias").prepend(Formatting.noIndent()).append(Formatting.oneSpace());
		formatter.property("name").append(Formatting.oneSpace());
	}

	private formatReferenceType(node: ReferenceType) {
		const formatter = this.getNodeFormatter(node);

		formatter.keywords("<", ".", "[").surround(Formatting.noSpace());
		formatter.keywords(">", "]").prepend(Formatting.noSpace());

		node.arguments.map((node, index) => {
			if (index !== 0) {
				formatter.node(node).prepend(Formatting.oneSpace());
			}
		});

		node.options.map((node, index) => {
			if (index !== 0) {
				formatter.node(node).prepend(Formatting.oneSpace());
			}
		});
	}

	private formatReferenceTypeOption(node: ReferenceTypeOption) {
		const formatter = this.getNodeFormatter(node);

		formatter.keyword(":").surround(Formatting.noSpace());
	}

	private formatReferenceDirective(node: ReferenceDirective) {
		const formatter = this.getNodeFormatter(node);

		formatter.keyword("#").append(Formatting.noSpace());
		formatter.keyword("(").surround(Formatting.noSpace());
		formatter.keyword(")").prepend(Formatting.noSpace());

		node.arguments.map((node, index) => {
			if (index !== 0) {
				formatter.node(node).prepend(Formatting.oneSpace());
			}
		});
	}

	private formatReferenceDirectiveArgument(node: ReferenceDirectiveArgument) {
		const formatter = this.getNodeFormatter(node);
		formatter.keyword(":").surround(Formatting.noSpace());
	}

	private formatField(node: Field) {
		const formatter = this.getNodeFormatter(node);
		const siblings = node.$container.fields;
		const max = Math.max(...siblings.map((node) => node.name.length));

		formatter.property("name").append(Formatting.spaces(max + 1 - node.name.length));
	}

	protected override format(node: AstNode): void {
		if (isModule(node)) this.formatModule(node);
		if (isImport(node)) this.formatImport(node);
		if (isImportMember(node)) this.formatImportMember(node);
		if (isScalar(node)) this.formatScalar(node);
		if (isScalarOption(node)) this.formatScalarOption(node);
		if (isEnum(node)) this.formatEnum(node);
		if (isEnumMember(node)) this.formatEnumMember(node);
		if (isStruct(node)) this.formatStruct(node);
		if (isException(node)) this.formatException(node);
		if (isEndpoint(node)) this.formatEndpoint(node);
		if (isDirective(node)) this.formatDirective(node);
		if (isDirectiveParameter(node)) this.formatDirectiveParameter(node);
		if (isAlias(node)) this.formatAlias(node);
		if (isReferenceType(node)) this.formatReferenceType(node);
		if (isReferenceTypeOption(node)) this.formatReferenceTypeOption(node);
		if (isReferenceDirective(node)) this.formatReferenceDirective(node);
		if (isReferenceDirectiveArgument(node)) this.formatReferenceDirectiveArgument(node);
		if (isField(node)) this.formatField(node);
	}
}
